import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music2, Check, Loader2, Upload } from 'lucide-react';
import { getHistory, AnalysisHistoryItem } from '../lib/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './HistoryPage.css';

export default function HistoryPage() {
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const result = await getHistory();
      setHistory(result);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="container">
          <div className="loading-state">
            <LoadingSpinner size="lg" color="cyan" />
            <p>Loading analysis history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="container">
        <header className="history-header">
          <h1>Analysis history</h1>
          <p>Your previously analyzed tracks</p>
        </header>

        {error && (
          <div className="error-message">{error}</div>
        )}

        {history.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon-wrap">
              <Music2 size={28} strokeWidth={1.5} />
            </div>
            <h2>No analyses yet</h2>
            <p>Upload and analyze your first track to get started.</p>
            <button
              className="upload-button"
              onClick={() => navigate('/')}
            >
              <Upload size={14} strokeWidth={2} />
              Upload track
            </button>
          </div>
        ) : (
          <div className="history-grid">
            {history.map((item) => (
              <div
                key={item.analysisId}
                className="history-card"
                onClick={() => navigate(`/results/${item.analysisId}`)}
              >
                <div className="card-header">
                  <div className="card-title">{item.filename}</div>
                  <div className={`card-status ${item.status}`}>
                    {item.status === 'completed'
                      ? <Check size={12} strokeWidth={2.4} />
                      : <Loader2 size={12} strokeWidth={2.4} className="spin" />}
                  </div>
                </div>
                <div className="card-meta">
                  {item.tempo && (
                    <div className="meta-item">
                      <span className="meta-label">BPM</span>
                      <span className="meta-value">{item.tempo.toFixed(1)}</span>
                    </div>
                  )}
                  {item.key && (
                    <div className="meta-item">
                      <span className="meta-label">Key</span>
                      <span className="meta-value">{item.key}</span>
                    </div>
                  )}
                  {item.durationSeconds && (
                    <div className="meta-item">
                      <span className="meta-label">Duration</span>
                      <span className="meta-value">{formatDuration(item.durationSeconds)}</span>
                    </div>
                  )}
                </div>
                <div className="card-footer">
                  <span className="card-date">{formatDate(item.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

